import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";

interface SearchContextData {
  searchTerm: string;
  searchIndustry: string;
  searchMixIdeal: string;
  searchProductType: string;
  searchCategoryGroup: string;
  searchCategory: string;
  setSearchTerm: (term: string, offset?: number) => void;
  setSearchIndustry: (industry: string, offset?: number) => void;
  setSearchMixIdeal: (mixIdeal: string, offset?: number) => void;
  setSearchProductType: (productType: string, offset?: number) => void;
  setSearchCategoryGroup: (categoryGroup: string, offset?: number) => void;
  setSearchCategory: (category: string, offset?: number) => void;
}

const SearchContext = createContext<SearchContextData>({} as SearchContextData);

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error("useSearchContext must be used within a SearchProvider");
  }
  return context;
};

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider = ({ children }: SearchProviderProps) => {
  const [searchTerm, setSearchTermState] = useState<string>("*");
  const [searchIndustry, setSearchIndustryState] = useState<string>("*");
  const [searchMixIdeal, setSearchMixIdealState] = useState<string>("*");
  const [searchProductType, setSearchProductTypeState] = useState<string>("*");
  const [searchCategoryGroup, setSearchCategoryGroupState] =
    useState<string>("*");
  const [searchCategory, setSearchCategoryState] = useState<string>("*");

  const setSearchTerm = useCallback((term: string, _offset?: number) => {
    setSearchTermState(term);
  }, []);

  const setSearchIndustry = useCallback(
    (industry: string, _offset?: number) => {
      setSearchIndustryState(industry);
    },
    []
  );

  const setSearchMixIdeal = useCallback(
    (mixIdeal: string, _offset?: number) => {
      setSearchMixIdealState(mixIdeal);
    },
    []
  );

  const setSearchProductType = useCallback(
    (productType: string, _offset?: number) => {
      setSearchProductTypeState(productType);
    },
    []
  );

  const setSearchCategoryGroup = useCallback(
    (categoryGroup: string, _offset?: number) => {
      setSearchCategoryGroupState(categoryGroup);
    },
    []
  );

  const setSearchCategory = useCallback(
    (category: string, _offset?: number) => {
      setSearchCategoryState(category);
    },
    []
  );

  return (
    <SearchContext.Provider
      value={{
        searchTerm,
        searchIndustry,
        searchMixIdeal,
        searchProductType,
        searchCategoryGroup,
        searchCategory,
        setSearchTerm,
        setSearchIndustry,
        setSearchMixIdeal,
        setSearchProductType,
        setSearchCategoryGroup,
        setSearchCategory,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
